import { FeatureFlagStore, LDProvider as BaseLDProvider } from '@servicetitan/launchdarkly-service';
import { provide } from '@servicetitan/react-ioc';
import { LDContext } from 'launchdarkly-js-client-sdk';
import { PropsWithChildren, useMemo } from 'react';

const clientKeys = {
    go: '6852e8c99e208c095d63f04b',
    stage: '69bda6aaa71e760a0a4b3b3b',
    dev: '6852e8c99e208c095d63f04a',
};

/*
 * Customer portal does not follow standard monolith env naming conventions
 * Return 'go' key for both production and next environments
 */
const getClientKeyForEnv = (): string => {
    const currentHostname = window.location.hostname;
    const productionHostnames = ['myservicetitan.com', 'next.servicetitan.com'];
    const stageHostNames = ['stage.servicetitan.com'];

    const isProduction = productionHostnames.some(hostname => currentHostname.includes(hostname));
    const isStage = stageHostNames.some(hostname => currentHostname.includes(hostname));

    return isProduction ? clientKeys.go : isStage ? clientKeys.stage : clientKeys.dev;
};

const clientSideID = getClientKeyForEnv();

/*
 * window.PortalSettings.tenantName is populated for both authorized and login-less sessions in
 * this shell (see portal-auth.store.ts, which reads it in both branches for FullStory identity),
 * so no separate login-less fallback is needed here. Note this shell's own login-less cookie
 * (ST-Customer-Portal-Frontend) carries { LoginType, Email, SignerRole } — a different payload
 * shape than the { TenantId, Tenant } cookie the mfe workspace's getLoginlessTenantName() parses —
 * so that helper would not apply here even if imported.
 */
const getTenantName = (): string | undefined => (window as any).PortalSettings?.tenantName;

/*
 * Skip mounting BaseLDProvider until a real tenant context is known, instead of evaluating flags
 * against getLDClient's anonymous-context fallback. BaseLDProvider initializes the LDClient once
 * on mount and never re-identifies on a later context change, so mounting early with an unknown
 * context would permanently strand the client on anonymous.
 *
 * FeatureFlagStore must still be bound in this outer wrapper when BaseLDProvider is skipped:
 * @servicetitan/launchdarkly-service@37 resolves WithFeatureFlag via DI, so authorized routes
 * (LegalAgreementGate, estimates deposit flow, etc.) crash without it.
 */
export const LDProvider = provide({
    singletons: [FeatureFlagStore],
})(({ children }: PropsWithChildren) => {
    const tenantName = getTenantName();

    const context: LDContext | undefined = useMemo(
        () => (tenantName ? { kind: 'tenant-name', key: String(tenantName) } : undefined),
        [tenantName]
    );

    if (!context) {
        return children;
    }

    return (
        <BaseLDProvider
            clientSideID={clientSideID}
            projectName="customer-portal-platform"
            context={context}
        >
            {children}
        </BaseLDProvider>
    );
});
